import { Box, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  downloadFile,
  getCarOverviewCsv,
  Language,
  Right,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { useUser } from '../../components/UserContext';
import { convertCarIntoTableEntries } from '../../utils/car/Car.utils';
import { isUserAllowedToDo } from '../../utils/user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

const CarOverview: React.FC = () => {
  const { user, axios, updateContextUser } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState<string>('');
  const [includeDeactivated, toggleIncludeDeactivated] = useState<boolean>(false);

  const { data: loadedCars, mutate: mutateAllCars } = useData('CARS_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedActiveCars, mutate: mutateActiveCars } = useData('CARS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedOffices } = useData('OFFICES_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedSimpleUsers } = useData('SIMPLEUSERS_ALL', {
    config: { fallbackData: [] },
  });

  //Holds TableRows for cars
  const rows: TableRow[] = useMemo(
    (): TableRow[] =>
      convertCarIntoTableEntries(
        user.right,
        includeDeactivated ? loadedCars : loadedActiveCars,
        navigate,
        loadedSimpleUsers,
        loadedOffices,
      ),
    [
      includeDeactivated,
      loadedActiveCars,
      loadedCars,
      loadedOffices,
      loadedSimpleUsers,
      navigate,
      user.right,
    ],
  );

  /**
   * Helper method to crete and download the overview csv
   */
  const handleCsvDownload = (): void => {
    getCarOverviewCsv(axios, user.language || Language.DE).then(
      (generatedFile) =>
        generatedFile && downloadFile(generatedFile, t('pages.carOverview.overviewFilename')),
    );
  };

  return (
    <>
      <TopBar title={t('pages.carOverview.headline')} onSearch={setFilterValue}>
        {isUserAllowedToDo(user.right, Right.CAR_CREATE) && (
          <>
            <IconComponent type={IconType.ADD} onClick={() => navigate('/cars/create')} />
            <IconComponent
              type={IconType.DISABLED}
              onClick={() => {
                !includeDeactivated ? mutateAllCars() : mutateActiveCars();
                toggleIncludeDeactivated(!includeDeactivated);
              }}
              style={{ color: includeDeactivated ? '#bc2e46' : '' }}
            />
            <IconComponent type={IconType.DOWNLOAD} onClick={handleCsvDownload} />
          </>
        )}
      </TopBar>
      <Box>
        <Table
          filterValue={filterValue}
          rows={rows}
          header={
            t('pages.carOverview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
          tableConfig={user.tableConfig?.get(TableKey.CAR_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(axios, user, TableKey.CAR_OVERVIEW, updatedHeader).then(
              (updatedUser) => updatedUser && updateContextUser(updatedUser),
            );
          }}
        />
      </Box>
    </>
  );
};
export default CarOverview;
